<template>
  <PlayerTemplate
    class="player-view"
    header-min-height="var(--player-header-height)"
    :toasts="toasts"
    :is-auth="isAuth"
    :header-links="headerLinks"
    :mobile-menu-open="false"
    :user="user"
    :footer-links="[
      {
        label: 'About',
        to: {
          name: staticRouteNames.About
        }
      },
      {
        label: 'Golf Operators',
        to: {
          name: staticRouteNames.OperatorsLanding
        }
      },
      {
        label: 'Cancellation Policy',
        to: {
          name: staticRouteNames.CancellationPolicy
        }
      },
      {
        label: 'Privacy Policy',
        to: { name: staticRouteNames.Privacy }
      }
    ]"
    :footer-social-links="[
      { icon: 'facebook', url: 'https://www.facebook.com/UpSwingGolfAB/' },
      {
        icon: 'instagram',
        url: 'https://www.instagram.com/upswing.golf/'
      },
      { icon: 'twitter', url: 'https://twitter.com/upswing_golf' }
    ]"
    @clickToast="handleClickToast"
    @add-event="onHeaderAddEvent"
  >
    <AddEventModal
      v-model:is-open="isAddEventOpen"
      :save-pending="addEventSaving"
      @save="handleAddEventSave"
    />
    <div class="player-view__main">
      <NotFound v-if="is404" />
      <div v-else class="margin-top--5 margin-bottom--5">
        <router-view v-slot="{ Component }">
          <!-- <transition name="fade" mode="out-in"> -->
          <!-- TODO switch to keep alive -->
          <!-- <keep-alive> -->
          <Suspense>
            <component v-if="Component" :is="Component" />
          </Suspense>
          <!-- </keep-alive> -->
          <!-- </transition> -->
        </router-view>
      </div>
    </div>
  </PlayerTemplate>
</template>

<script lang="ts" setup>
import { computed, defineComponent, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'

import { useClient } from '@base/api/useClient'
import { ToastSemantic } from '@base/_types/Toast'
import AddEventModal from '@base/event-add/AddEventModal.vue'
import { CREATE_EVENT_STEPPED_MUTATION } from '@base/event-add/api/mutations/createEventStepped'
import { useRouterStore } from '@base/store/routerStore'
import { useToastsStore } from '@base/store/toastsStore'
import { useUserStore } from '@base/store/userStore'

import { createEventStepped } from '__generated__/createEventStepped'

import NotFound from '@player/static/NotFound'
import PlayerTemplate from '@player/_components/PlayerTemplate/PlayerTemplate.vue'
import { clubsRouteNames } from '@player/clubs/router/routeNames'
import { coursesRouteNames } from '@player/courses/router/routeNames'
import { eventsRouteNames } from '@player/events/router/routeNames'
import { staticRouteNames } from '@player/static/router/routeNames'
import { usersRouteNames } from '@player/users/router/routeNames'

interface User {
  firstName: string
  lastName: string
  profilePicture?: string
  profilePictureSrcset?: string
  activeMembership?: boolean
  pointsBalance?: number
}

defineComponent({
  name: 'PlayerView'
})

const router = useRouter()
const client = useClient()
const toastsStore = useToastsStore()
const { toasts, removeToast } = toRefs(toastsStore)

const handleClickToast = (id: string) => {
  removeToast.value(id)
}

const isAddEventOpen = ref(false)
const addEventSaving = ref(false)

const headerLinks = computed(() => [
  {
    label: 'Courses',
    to: { name: coursesRouteNames.Courses }
  },
  {
    label: 'Indoor Golf',
    to: { name: clubsRouteNames.IndoorGolf }
  },
  {
    label: 'Events',
    to: { name: eventsRouteNames.Events }
  },
  {
    label: 'Add Event',
    action: 'add-event' as const,
    icon: 'plus'
  }
])

function onHeaderAddEvent() {
  if (!isAuth.value) {
    void router.push({ name: usersRouteNames.SignIn })
    return
  }
  isAddEventOpen.value = true
}

async function handleAddEventSave(payload: {
  clubId: string
  eventName: string
  isNonGolf: boolean
}) {
  if (addEventSaving.value) return
  addEventSaving.value = true
  try {
    const { data } = await client.mutate<createEventStepped>({
      mutation: CREATE_EVENT_STEPPED_MUTATION,
      variables: {
        eventName: payload.eventName,
        clubId: payload.clubId,
        isNonGolf: payload.isNonGolf
      }
    })

    if (
      !data ||
      data.createEvent.__typename !== 'CreateEventOutput' ||
      !data.createEvent.event
    ) {
      toastsStore.pushToast({
        content: 'Could not create the event. Please try again.',
        icon: 'exclamation-square',
        semantic: ToastSemantic.Error
      })
      return
    }

    const newId = data.createEvent.event.id
    isAddEventOpen.value = false

    await router.push({
      name: eventsRouteNames.EventDetail,
      params: { eventId: newId }
    })
  } catch (e) {
    console.error(e)
    toastsStore.pushToast({
      content: 'Could not create the event. Please try again.',
      icon: 'exclamation-square',
      semantic: ToastSemantic.Error
    })
  } finally {
    addEventSaving.value = false
  }
}

const routerStore = toRefs(useRouterStore())
const { is404 } = routerStore

const userStore = toRefs(useUserStore())
const {
  isAuth,
  firstName,
  lastName,
  profilePicture,
  profilePictureSrcset,
  membership,
  pointsBalance
} = userStore

const user = computed(() => {
  const userObj: User = {
    firstName: '',
    lastName: '',
    profilePicture: '',
    profilePictureSrcset: '',
    activeMembership: false,
    pointsBalance: 0
  }
  if (isAuth) {
    userObj.firstName = firstName.value
    userObj.lastName = lastName.value
    userObj.profilePicture = profilePicture.value
    userObj.profilePictureSrcset = profilePictureSrcset.value
    userObj.activeMembership = membership.value.active
    userObj.pointsBalance = pointsBalance.value
  }
  return userObj
})
</script>

<style lang="scss" scoped>
.player-view {
  &__main {
    // min-height: calc(100vh - var(--player-header-height));
  }
}
</style>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
